import { generateColors } from "../../../../../utils/chartColors";

function configs(labels, datasets, yAxisConfig = null, profileColorMap = {}, yAxisTitle = "") {
  const allValues = datasets.flatMap(dataset => dataset.data).filter(value => value !== null && value !== undefined);

  const minValue = allValues.length > 0 ? Math.min(...allValues) : 0;
  const maxValue = allValues.length > 0 ? Math.max(...allValues) : 10;
  const range = maxValue - minValue;

  // console.log("Incoming datasets : ", datasets);

  let stepSize;
  const allIntegers = allValues.every((val) => Number.isInteger(val));

  if (range < 5) {
    stepSize = range > 0 ? range / 5 : 1;
    if (allIntegers) stepSize = Math.ceil(stepSize);
  } else {
    stepSize = Math.ceil(range / 5);
  }

  // console.log("Min Value : ", minValue);
  // console.log("Max Value : ", maxValue);

  const yPaddingRatio = 0.05;
  const paddingAmount = range > 0 ? range * yPaddingRatio : 1;

  const adjustedMin = yAxisConfig?.min !== undefined
    ? yAxisConfig.min
    : minValue === maxValue
      ? minValue - 1
      : Math.floor((minValue - paddingAmount) / stepSize) * stepSize;

  const adjustedMax = yAxisConfig?.max !== undefined
    ? yAxisConfig.max
    : minValue === maxValue
      ? maxValue + 1
      : Math.ceil((maxValue + paddingAmount) / stepSize) * stepSize;

  const profileIds = datasets.map(dataset => dataset.profileId || dataset.label);

  // Fallback generation (legacy support or missing IDs)
  const generatedColors = generateColors(datasets.length, profileIds);

  const coloredDatasets = datasets.map((dataset, index) => {
    // 1. Check if specific overrides exist (e.g. Gain/Loss bars)
    if (dataset.backgroundColor && dataset.borderColor) {
      return dataset;
    }

    // 2. Check Global Map (Priority)
    let assignedColor = null;
    if (dataset.profileId && profileColorMap && profileColorMap[dataset.profileId]) {
      assignedColor = profileColorMap[dataset.profileId];
    } else {
      // 3. Fallback to local generation
      assignedColor = generatedColors[index];
    }

    return {
      ...dataset,
      borderColor: dataset.borderColor || assignedColor,
      backgroundColor: dataset.backgroundColor || assignedColor,
    };
  });

  return {
    data: {
      labels,
      datasets: coloredDatasets,
    },
    options: {
      animation: false,
      spanGaps: true,
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { top: 0, bottom: 0 },
      },
      plugins: {
        tooltip: {
          mode: "index",
          intersect: false,
          callbacks: {
            label: function (tooltipItem) {
              const dataset = tooltipItem.dataset;
              const profileName = dataset.label || "Unknown Profile";
              const value = tooltipItem.raw?.toLocaleString();

              // Use the index to check prediction flag
              const dataIndex = tooltipItem.dataIndex;
              const isPredicted = dataset.isPredictedFlags?.[dataIndex];

              // Add a suffix if this data point used predicted followers
              const suffix = isPredicted ? " (Predicted)" : "";

              return `${profileName}: ${value}${suffix}`;
            },

          },
        },
        legend: { display: true },
      },
      scales: {
        x: {
          offset: true,
          ticks: {
            autoSkip: false,
            maxRotation: 0,
            minRotation: 0,
            align: "center",
            padding: 10,
            callback: function (value, index, values) {
              const totalLabels = values.length;
              const chartWidth = this.chart.width;
              const maxVisibleLabels = Math.floor(chartWidth / 50);

              const rawDate = this.getLabelForValue(value);
              const date = new Date(rawDate);
              if (isNaN(date)) return rawDate;

              if (totalLabels <= maxVisibleLabels || index % Math.ceil(totalLabels / maxVisibleLabels) === 0) {
                return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
              }

              return "";
            },
          },
          grid: {
            drawBorder: false,
            drawOnChartArea: false,
            drawTicks: true,
            tickLength: 5,
          },
        },
        y: {
          display: true, // Ensure axis is shown
          title: {
            display: !!yAxisTitle,
            text: yAxisTitle,
            color: "#999",
            font: {
              size: 12,
              style: 'normal',
              weight: 'bold',
            },
            padding: { bottom: 0 }
          },
          min: adjustedMin,
          max: adjustedMax,
          ticks: {
            stepSize: yAxisConfig?.ticks?.stepSize || stepSize,
            autoSkip: yAxisConfig?.ticks?.autoSkip !== undefined ? yAxisConfig.ticks.autoSkip : true,
            // ✅ FIX: Use the custom callback if provided, otherwise use the default
            callback: yAxisConfig?.ticks?.callback || (value => value.toLocaleString()),
            // ✅ FIX: Add an explicit tick at 50 to ensure the neutral label is always displayed
            // This can be done by adjusting the y axis configuration in the wrapper component
          },
          grid: {
            drawBorder: false,
            tickLength: 0,
            borderDash: [5, 5],
            color: "#c1c4ce5c",
          },
        },
      },
    },
  };
}

export default configs;
